/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Spin } from 'antd';
import {
  Close,
  ExpandIcon,
  MinimizeIcon,
  ShrinkIcon,
  vncPlaceholder
} from 'assets/images';
import { TRANSLATION_NAMESPACES } from 'util/translation/constant';
import * as selector from './state/selector';
import * as operation from './state/operation';
import { MachineVncExpirationModal } from './MachineVncExpirationModal';
import { MachineVncCheckedFirmwareModal } from './MachineVncCheckedFirmwareModal';
import { MachineVncStatusModal } from './MachineVncStatusModal';
import { VNC_SESSION_STATUS } from './util/constant';

export const MachineVnc = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_NAMESPACES.MACHINE_DETAILS);
  window.vncConnectDelayTimeoutInMiliseconds = 2000;
  const pollStatusTriesRef = useRef(1);
  const pollStatusIntervalRef = useRef(null);
  const nowIntervalRef = useRef(null);
  const [now, setNow] = useState(new Date().toISOString());

  const { machineVncSession } = useSelector((state) => ({
    machineVncSession: selector.selectMachineVncSession(state)
  }));
  const { vnc, status, data, id, expiresAt, isLoading, isDoneCalling } =
    machineVncSession;

  const machineIdLocalStored = localStorage.getItem('current-machine-session');

  const hasCachedEnabled = () => {
    if (machineIdLocalStored) {
      return true;
    }

    return false;
  };

  const isVNCSessionOpened = status.data && hasCachedEnabled();

  const isCheckingSessionCachedStatus = isLoading && hasCachedEnabled();

  /**
   * @context initialing a new VNC session
   * @description Handle action to show timeout message when initialing a new VNC session
   */
  const handleHideMachineVncStatusModal = () => {
    dispatch(operation.hideMachineVncSessionStatusModal());
    onClose();
  };

  /**
   * @context VNC session is still valid but user force to hide the Iframe
   * @description Handle disconnect VNC session when cliking on "Exit remote control"
   *              in this case, the current session will be hidden
   *              and the status of this session will be cached in both local storage and VNC server)
   */
  const handleDisconnectVncSession = useCallback(() => {
    pollStatusTriesRef.current = 1;
    localStorage.removeItem('current-machine-session');
    clearInterval(nowIntervalRef.current);
    clearInterval(pollStatusIntervalRef.current);
    dispatch(operation.resetMachineVncSession());
    dispatch(operation.resetMachineVncSessionStatus());
    dispatch(operation.resetCheckVncCachedUrlSession());
  }, [dispatch]);

  const onClose = useCallback(() => {
    dispatch(operation.activeClose());
    handleDisconnectVncSession();
  }, [dispatch, handleDisconnectVncSession]);

  const onMinimize = useCallback(() => {
    dispatch(operation.activeMinimize());
  }, [dispatch]);

  const onUnminimize = useCallback(() => {
    dispatch(operation.activeUnminimize());
  }, [dispatch]);

  const onActiveExpand = useCallback(() => {
    dispatch(operation.activeExpand());
  }, [dispatch]);

  const onDeactiveExpand = useCallback(() => {
    dispatch(operation.activeUnexpand());
  }, [dispatch]);

  const onCachedOpen = useCallback(() => {
    dispatch(operation.activeCachedOpen());
  }, [dispatch]);

  const isVncSessionLaunching =
    isCheckingSessionCachedStatus || isLoading || status.isLoading;

  /**
   * @context Component unmount
   */
  useEffect(
    () => () => {
      dispatch(operation.resetMachineVncSession());
      dispatch(operation.resetMachineVncSessionStatus());
      dispatch(operation.resetCheckVncCachedUrlSession());
      dispatch(operation.resetVncUI());
    },
    [dispatch]
  );

  /**
   * @context Hanlde polling status of VNC session while initialing a new session
   *          (button "Access remote control" is clicked)
   */
  useEffect(() => {
    if (data) {
      pollStatusIntervalRef.current = setInterval(() => {
        if (
          pollStatusTriesRef.current <= 1200 &&
          !status.statusResponse.includes(VNC_SESSION_STATUS.RUNNING) &&
          !status.statusResponse.includes(VNC_SESSION_STATUS.FAILED)
        ) {
          pollStatusTriesRef.current += 1;
          dispatch(operation.getMachineVncSessionStatus(id));
        }
      }, 500);
    } else {
      clearInterval(pollStatusIntervalRef.current);
    }

    return () => clearInterval(pollStatusIntervalRef.current);
  }, [dispatch, data, id, status.statusResponse, isDoneCalling]);

  /**
   * @context Update the component state once the vnc session is ready to connect
   */
  useEffect(() => {
    if (data) {
      if (
        status.statusResponse.includes(VNC_SESSION_STATUS.RUNNING) ||
        status.statusResponse.includes(VNC_SESSION_STATUS.FAILED) ||
        status.statusResponse.length === 1200
      ) {
        setTimeout(() => {
          dispatch(
            operation.pollMachineVncSessionStatusResult(status.statusResponse)
          );
        }, window.vncConnectDelayTimeoutInMiliseconds);
      }
    }
  }, [dispatch, status.statusResponse, data]);

  /**
   * @description initialize a timer to be call every second to update the current time of the system
   */
  useEffect(() => {
    nowIntervalRef.current = setInterval(() => {
      setNow(new Date().toISOString());
    }, 1000);

    return () => clearInterval(nowIntervalRef.current);
  });

  /**
   * @description to be executed every second to validate the session expiration state
   */
  useEffect(() => {
    if (now >= expiresAt) {
      if (isVNCSessionOpened) {
        dispatch(operation.showMachineVncExpirationModal());
      }

      dispatch(operation.resetMachineVncSessionStatus());
      dispatch(operation.resetMachineVncSession());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now]);

  /**
   * @description Double check if the session is cached in VNC server
   */
  useEffect(() => {
    if (hasCachedEnabled() && !data) {
      dispatch(operation.requestMachineVncSession(machineIdLocalStored));
      onCachedOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @description Close vnc session container when user can not get session endpoint
   */
  useEffect(() => {
    if (!data && isDoneCalling) {
      dispatch(operation.resetMachineVncSession());
      onClose();
      localStorage.removeItem('current-machine-session');
    }
  }, [dispatch, data, isDoneCalling, onClose, id]);

  const renderDrawerExtra = () => (
    <>
      <Button
        className="minimize-button"
        icon={<MinimizeIcon />}
        onClick={vnc.minimize ? onUnminimize : onMinimize}
        type="link"
      />
      {vnc.expand ? (
        <Button
          className="shrink-button"
          icon={<ShrinkIcon />}
          onClick={onDeactiveExpand}
          type="link"
        />
      ) : (
        <Button
          className="expand-button"
          icon={<ExpandIcon />}
          onClick={onActiveExpand}
          type="link"
        />
      )}
      <Button
        style={{ width: 'fit-content' }}
        className="close-button"
        icon={<Close />}
        onClick={onClose}
        type="link"
      />
    </>
  );

  const renderLoadingText = useCallback(() => {
    if (
      (isLoading || status.isLoading) &&
      status.code &&
      !status.statusResponse.includes(VNC_SESSION_STATUS.FAILED) &&
      !status.statusResponse.includes(VNC_SESSION_STATUS.RUNNING)
    ) {
      return `machineVncPanel.${status.code[0]}`;
    }
    return 'machineVncPanel.loading';
  }, [isLoading, status.code, status.isLoading, status.statusResponse]);

  return (
    <>
      {vnc.open && (
        <div className="machine-vnc-new">
          <div
            className={`
            machine-vnc-new__container
            ${vnc.minimize ? 'minimize' : ''} 
            ${vnc.expand ? 'expand' : ''}`}>
            <Drawer
              title={t('machineVncPanel.title')}
              placement="bottom"
              closable={false}
              getContainer={false}
              mask={false}
              onClose={onClose}
              destroyOnClose
              open={vnc.open}
              extra={renderDrawerExtra()}>
              <div className="machine-vnc-session__body__vnc-session--container">
                {isCheckingSessionCachedStatus ? (
                  <Spin
                    spinning={isVncSessionLaunching}
                    size="large"
                    className="spinning">
                    <p className="machine-vnc-session__body__vnc-session--container__loading-text">
                      {t(renderLoadingText())}
                    </p>
                    <p className="machine-vnc-session__body__vnc-session--container__waiting-text">
                      {t('machineVncPanel.waitingText')}
                    </p>
                    <img src={vncPlaceholder} alt="vnc-placeholder" />
                  </Spin>
                ) : (
                  <>
                    {!isVNCSessionOpened ? (
                      <Spin
                        spinning={isVncSessionLaunching}
                        size="large"
                        className="spinning">
                        <p className="machine-vnc-session__body__vnc-session--container__loading-text">
                          {t(renderLoadingText())}
                        </p>
                        <p className="machine-vnc-session__body__vnc-session--container__waiting-text">
                          {t('machineVncPanel.waitingText')}
                        </p>
                        <img src={vncPlaceholder} alt="vnc-placeholder" />
                      </Spin>
                    ) : (
                      <iframe
                        className="machine-vnc-session__body__vnc-session"
                        src={data}
                        title="VNC session for controlling machine remotely"
                      />
                    )}
                  </>
                )}
              </div>
            </Drawer>
          </div>
        </div>
      )}

      {machineVncSession.modal.firmwareChecking.visible && (
        <MachineVncCheckedFirmwareModal />
      )}

      {machineVncSession.modal.status.visible && (
        <MachineVncStatusModal onClose={handleHideMachineVncStatusModal} />
      )}

      {machineVncSession.modal.expiration.visible && (
        <MachineVncExpirationModal />
      )}
    </>
  );
};
